import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"

const t = (key, options) => I18n.t(`project_planning.guide.${key}`, options)
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "")

const french = () => (I18n.locale || "fr").startsWith("fr")
const KEYS = {
  alt:    isMac ? "⌥" : "Alt",
  shift:  "⇧",
  cmd:    isMac ? "⌘" : "Ctrl",
  delete: isMac ? "⌫" : (french() ? "Suppr" : "Del"),
  escape: french() ? "Échap" : "Esc",
}

const Key = ({ name }) => (
  <kbd className="inline-grid place-items-center min-w-[22px] h-[22px] px-1.5 rounded-md border border-slate-300 border-b-2 bg-white font-sans text-[11px] font-semibold text-slate-600">
    {KEYS[name] || name}
  </kbd>
)

const SECTIONS = [
  { key: "assign", items: [
    { key: "drag_project", icon: "hand-pointer" },
    { key: "drag_team",    icon: "users" },
    { key: "click_cell",   icon: "pen-to-square" },
    { key: "range",        icon: "arrows-left-right", keys: ["shift"] },
    { key: "drag_person",  icon: "user-plus" },
  ] },
  { key: "rework", items: [
    { key: "move",         icon: "up-down-left-right" },
    { key: "duplicate",    icon: "clone", keys: ["alt"] },
    { key: "edit",         icon: "sliders" },
    { key: "select",       icon: "object-group", keys: ["shift", "cmd"] },
    { key: "copy_week",    icon: "copy" },
  ] },
  { key: "read", items: [
    { key: "highlight",    icon: "highlighter" },
    { key: "idle",         icon: "user-clock" },
    { key: "markers",      icon: "circle-exclamation" },
  ] },
]

const Item = ({ item }) => (
  <li className="flex gap-3 py-2.5">
    <span className="grid place-items-center w-8 h-8 shrink-0 rounded-lg bg-slate-100 text-slate-500">
      <FontAwesomeIcon icon={item.icon} className="text-[13px]" />
    </span>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-slate-900">
        {t(`items.${item.key}.title`)}
        {(item.keys || []).map((name, index) => (
          <React.Fragment key={name}>
            {index > 0 && <span className="text-[11px] font-normal text-slate-400">{t("or")}</span>}
            <Key name={name} />
          </React.Fragment>
        ))}
      </div>
      <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-600">{t(`items.${item.key}.text`, KEYS)}</p>
    </div>
  </li>
)

const GuideModal = ({ isOpen, onClose }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    size="lg"
    title={t("title")}
    footer={<Button variant="primary" onClick={onClose}>{t("close")}</Button>}
  >
    <p className="text-[13px] text-slate-600 mb-3">{t("intro")}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
      {SECTIONS.map(section => (
        <section key={section.key} className={section.key === "read" ? "md:col-span-2" : undefined}>
          <h3 className="mt-2 mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{t(`sections.${section.key}`)}</h3>
          <ul className={section.key === "read" ? "grid grid-cols-1 md:grid-cols-3 gap-x-6 divide-slate-100" : "divide-y divide-slate-100"}>
            {section.items.map(item => <Item key={item.key} item={item} />)}
          </ul>
        </section>
      ))}
    </div>
  </Modal>
)

export default GuideModal
